/**
 * Starting a programme: from nothing, or from an exported document.
 *
 * The two routes in were tabs over one form, which promised panels that stay
 * put while you move between them. They are a choice that changes the form, so
 * they are radios in the form they change.
 *
 * @module components/programmes/CreateProgrammeForm
 */

import React, { useId } from "react";

import {
  Alert,
  Button,
  Combobox,
  FieldSet,
  FileField,
  FormField,
  IconButton,
  KeyValue,
  MutedText,
  PageHeader,
  Panel,
  RadioCardGroup,
  TextField,
} from "@/components/ui";

export type ProgrammeCreationMode = "scratch" | "import";

export interface InitialModuleDraft {
  id: string;
  title: string;
}

export interface ImportSummary {
  fileName: string;
  moduleCount: number;
  versionCount: number;
  schemaVersion: number;
}

export interface LeadOption {
  id: string;
  displayName: string;
  email?: string;
}

export interface CreateProgrammeFormProps {
  mode: ProgrammeCreationMode;
  onModeChange: (mode: ProgrammeCreationMode) => void;
  title: string;
  onTitleChange: (title: string) => void;
  /** Present when the account chooses the lead; absent when it is the lead. */
  leads?: readonly LeadOption[];
  leadId?: string;
  onLeadChange?: (leadId: string) => void;
  /** The lead's own name, for an account that leads what it creates. */
  leadName?: string;
  initialModules: readonly InitialModuleDraft[];
  onAddModule: () => void;
  onModuleTitleChange: (id: string, title: string) => void;
  onRemoveModule: (id: string) => void;
  importSummary?: ImportSummary;
  onImportFile: (file: File | null) => void;
  importError?: string;
  submitting: boolean;
  error?: string;
  onSubmit: () => void;
  onCancel: () => void;
}

/**
 * @example
 * ```tsx
 * <CreateProgrammeForm mode={mode} onModeChange={setMode} … />
 * ```
 */
/**
 * Move focus off a row that is about to unmount.
 *
 * The neighbouring row's remove button if there is one, otherwise the control
 * that adds a row. Without it the browser drops focus to the body and the
 * next Tab restarts at the top of the page.
 */
function focusAfterRemoval(button: HTMLElement): void {
  const row = button.closest("li");
  const neighbour = row?.nextElementSibling ?? row?.previousElementSibling;
  const fallback = row
    ?.closest("form")
    ?.querySelector<HTMLElement>('[data-testid="add-initial-module"]');
  (neighbour?.querySelector<HTMLElement>("button") ?? fallback)?.focus();
}

export function CreateProgrammeForm({
  mode,
  onModeChange,
  title,
  onTitleChange,
  leads = undefined,
  leadId = "",
  onLeadChange = undefined,
  leadName = undefined,
  initialModules,
  onAddModule,
  onModuleTitleChange,
  onRemoveModule,
  importSummary = undefined,
  onImportFile,
  importError = undefined,
  submitting,
  error = undefined,
  onSubmit,
  onCancel,
}: CreateProgrammeFormProps): React.ReactElement {
  const chooseLead = leads !== undefined;
  // Named rather than merely counted: a disabled submit with required fields
  // never runs the browser's own validation, so without this the form refuses
  // to go and says nothing about why.
  const missingId = useId();
  const missing: string[] = [];
  if (mode === "import" && !importSummary) {
    missing.push("a programme JSON document");
  }
  if (title.trim().length === 0) {
    missing.push("a programme title");
  }
  if (chooseLead && !leadId) {
    missing.push("a programme lead");
  }
  // Only what this mode renders: the drafts survive a mode change on purpose,
  // and the import path ignores them, so naming one here blocked a valid
  // import from a screen showing no module to name.
  if (mode === "scratch" && initialModules.some((module) => module.title.trim().length === 0)) {
    missing.push("a name for every module");
  }
  const canSubmit = missing.length === 0;

  return (
    <form
      className="flex max-w-3xl flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        // The submit is `aria-disabled`, not `disabled`, so it no longer
        // suppresses implicit submission, and the button's guard sees only a
        // press. `submitting` too: overlapping runs are allowed, so a second
        // submit creates a second thing under a fresh id.
        if (!canSubmit || submitting) {
          return;
        }
        onSubmit();
      }}
      data-testid="create-programme-form"
    >
      <PageHeader
        title="Start a new programme"
        description="Name it and choose its lead. You add everything else afterwards."
      />

      {error ? <Alert tone="warning">{error}</Alert> : null}

      <Panel>
        <RadioCardGroup
          legend="Programme creation method"
          value={mode}
          onChange={onModeChange}
          options={[
            {
              value: "scratch",
              label: "Start from scratch",
              description: "Creates an empty programme.",
              icon: "file-plus",
              "data-testid": "programme-create-scratch-tab",
            },
            {
              value: "import",
              label: "Import programme",
              description: "Create a programme from an exported JSON document.",
              icon: "upload-simple",
              "data-testid": "programme-create-import-tab",
            },
          ]}
        />
      </Panel>

      {mode === "import" && (
        <Panel data-testid="programme-import-panel">
          <FieldSet
            legend="Programme JSON"
            description="The imported content is migrated and saved as a new programme."
          >
            <FileField
              label="Programme JSON"
              accept=".json,application/json"
              fileName={importSummary?.fileName}
              onChange={onImportFile}
              // Closed while the create runs: resetting the action's error
              // cannot cancel a run already in flight, so a pick made during
              // one races it to the alert region and both are assertive.
              disabled={submitting}
              error={importError}
              data-testid="new-programme-import-input"
            />
          </FieldSet>
          {importSummary && (
            <div className="mt-5" data-testid="programme-import-summary">
              <KeyValue
                entries={[
                  { label: "File", value: importSummary.fileName },
                  { label: "Modules", value: importSummary.moduleCount },
                  { label: "Versions", value: importSummary.versionCount },
                  { label: "Schema", value: `v${importSummary.schemaVersion}` },
                ]}
              />
            </div>
          )}
        </Panel>
      )}

      <Panel>
        <FieldSet legend="Programme details" columns={2}>
          <FormField
            label="Programme title"
            htmlFor="programme-shell-title"
            helpText="Use the formal title that appears in governance records and exports."
            required
          >
            <TextField
              id="programme-shell-title"
              value={title}
              onChange={onTitleChange}
              placeholder="e.g. BSc Applied Computing"
              required
              data-testid="programme-shell-title"
            />
          </FormField>
          {chooseLead ? (
            <FormField
              label="Programme lead"
              htmlFor="programme-shell-lead"
              helpText="The lead governs workflow progression and approvals."
              required
            >
              <Combobox
                id="programme-shell-lead"
                value={leadId}
                onChange={(value) => onLeadChange?.(value)}
                options={leads.map((lead) => ({
                  value: lead.id,
                  label: lead.displayName,
                  hint: lead.email,
                }))}
                placeholder="Select a programme lead"
                emptyMessage="No staff match"
                disabled={leads.length === 0}
                required
                data-testid="programme-shell-lead"
              />
            </FormField>
          ) : (
            <FormField label="Programme lead" htmlFor="programme-shell-lead-name">
              <TextField
                id="programme-shell-lead-name"
                value={leadName ?? ""}
                onChange={() => undefined}
                readOnly
              />
            </FormField>
          )}
        </FieldSet>
      </Panel>

      {mode === "scratch" && (
        <Panel
          title="Initial modules"
          description="Optional. CAS components are chosen from the award rules afterwards."
          actions={
            <Button size="sm" icon="plus" onClick={onAddModule} data-testid="add-initial-module">
              Add module
            </Button>
          }
        >
          {initialModules.length === 0 ? (
            <MutedText>None yet. Modules can also be added later.</MutedText>
          ) : (
            <ul className="flex flex-col gap-4">
              {initialModules.map((module, index) => (
                <li key={module.id} className="flex items-end gap-3">
                  <FormField
                    label={`Module name ${index + 1}`}
                    htmlFor={`initial-module-title-${module.id}`}
                    className="flex-1"
                    required
                  >
                    <TextField
                      id={`initial-module-title-${module.id}`}
                      value={module.title}
                      onChange={(value) => onModuleTitleChange(module.id, value)}
                      placeholder="e.g. Applied Databases"
                      required
                      data-testid={`initial-module-title-${index + 1}`}
                    />
                  </FormField>
                  <IconButton
                    icon="trash"
                    tone="danger"
                    aria-label={`Remove module ${index + 1}`}
                    onClick={(event) => {
                      // Focus first. The button lives inside the row it
                      // deletes, so unmounting it drops focus to the body and
                      // the next Tab restarts at the top of the page.
                      focusAfterRemoval(event.currentTarget);
                      onRemoveModule(module.id);
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {/* Named on the button as well as shown beside it. A disabled submit
            runs none of the browser's own validation and is out of the tab
            order, so text merely sitting near it explains nothing. */}
        {missing.length > 0 && (
          <div id={missingId} aria-live="polite" className="mr-auto">
            <MutedText size="caption" data-testid="create-programme-missing">
              Still needed: {missing.join(", ")}.
            </MutedText>
          </div>
        )}
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          icon="plus-circle"
          loading={submitting}
          loadingLabel="Creating the programme"
          aria-describedby={missing.length > 0 ? missingId : undefined}
          aria-disabled={!canSubmit || undefined}
        >
          {mode === "import"
            ? "Create imported programme and continue"
            : "Create programme and continue"}
        </Button>
      </div>
    </form>
  );
}
